import api from '@/services/api';
import { Module, ActionContext } from 'vuex';
import { RootState } from '@/types/store';
import {
  AuthState,
  User,
  Credentials,
  LoginResponse,
  UserResponse,
} from '@/types/auth-types';
import { TokenService } from '@/services/tokenService';
import TokenWatcher from '@/services/tokenWatcher';

// Inicijalno stanje
const state: AuthState = {
  token: TokenService.getAccessToken(),
  user: TokenService.getUser(),
  isLoggedIn: !!TokenService.getAccessToken(),
  loginError: null,
  isLoading: false,
};

// Getter-i
const getters = {
  isAuthenticated: (state: AuthState): boolean => {
    console.log('[Auth Getter] isAuthenticated:', !!state.token);
    return !!state.token;
  },
  currentUser: (state: AuthState): User | null => {
    console.log('[Auth Getter] currentUser called, returning:', state.user);
    return state.user;
  },
};

// Pomoćne funkcije za ekstraktovanje podataka iz različitih formata odgovora
const extractTokenAndUser = (response: any): { token: string; user: User } => {
  console.log('[Auth Helper] Ekstraktovanje token/user iz odgovora:', response);

  // Ako odgovor ima klasičnu strukturu { token, user }
  if (response.token && response.user) {
    console.log('[Auth Helper] Odgovor ima direktnu { token, user } strukturu');
    return { token: response.token, user: response.user };
  }

  // Ako odgovor ima strukturu { status, message, data } gde je data objekat koji sadrži token i user
  if (
    response.status === 'success' &&
    response.data &&
    response.data.token &&
    response.data.user
  ) {
    console.log(
      '[Auth Helper] Odgovor ima { status, data: { token, user } } strukturu'
    );
    return { token: response.data.token, user: response.data.user };
  }

  // Ako odgovor ima strukturu { status, message, data } gde je data direktno user objekat
  if (response.status === 'success' && response.data && !response.data.token) {
    console.log(
      '[Auth Helper] Odgovor ima { status, data: user } strukturu, ali token nedostaje'
    );
    // Koristimo trenutni token iz state-a ako postoji
    const token = state.token || '';
    return { token, user: response.data };
  }

  // Ako ne možemo prepoznati strukturu, logujemo grešku
  console.error('[Auth Helper] Nepoznata struktura odgovora:', response);
  throw new Error('Nevalidan format API odgovora');
};

// Akcije
const actions = {
  async login(
    { commit, dispatch }: ActionContext<AuthState, RootState>,
    credentials: Credentials
  ) {
    console.log('[Auth Action] login started with:', {
      email: credentials.email,
    });
    try {
      commit('SET_LOADING', true);
      commit('SET_LOGIN_ERROR', null);

      const response = await api.post<LoginResponse>(
        '/api/auth/login',
        credentials
      );

      // Ekstraktovanje token-a i user-a iz različitih formata odgovora
      const { token, user } = extractTokenAndUser(response.data);

      localStorage.setItem('token', token);
      console.log('[Auth Action] Token sačuvan u localStorage');

      commit('SET_TOKEN', token);
      commit('SET_USER', user);
      commit('SET_LOGGED_IN', true);
      commit('SET_LOADING', false);

      console.log(
        '[Auth Action] Login uspešno završen, user postavljen:',
        user
      );

      // ✅ NOVO: Učitaj notifikacije odmah nakon uspešnog login-a
      try {
        console.log('[Auth Action] Loading notifications after login...');
        await dispatch('notifications/loadNotifications', null, { root: true });
        console.log(
          '[Auth Action] Notifications loaded successfully after login'
        );
      } catch (notificationError) {
        console.error(
          '[Auth Action] Error loading notifications after login:',
          notificationError
        );
        // Ne prekidaj login proces zbog greške sa notifikacijama
      }

      return response;
    } catch (error) {
      console.error('[Auth Action] Login error:', error);
      commit('SET_LOADING', false);
      commit(
        'SET_LOGIN_ERROR',
        error instanceof Error ? error.message : 'Login failed'
      );
      throw error;
    }
  },

  async logout({ commit }: ActionContext<AuthState, RootState>) {
    try {
      // Optional: Call logout endpoint
      // await api.post('/api/auth/logout')

      localStorage.removeItem('token');
      console.log('[Auth Action] Token removed from localStorage');

      commit('SET_TOKEN', null);
      commit('SET_USER', null);
      commit('SET_LOGGED_IN', false);
      console.log('[Auth Action] Logout completed');
    } catch (error) {
      console.error('[Auth Action] Logout error:', error);
    }
  },

  async fetchCurrentUser({
    commit,
    dispatch,
    state,
  }: ActionContext<AuthState, RootState>) {
    try {
      const response = await api.get<UserResponse>('/api/auth/me');

      // Ekstraktovanje podataka korisnika iz različitih formata odgovora
      let userData: User;

      // Ako odgovor ima strukturu { status, message, data }
      if (response.data.status === 'success' && response.data.data) {
        userData = response.data.data;
      } else {
        // Inače, pretpostavljamo da je odgovor direktno User objekat
        userData = response.data as unknown as User;
      }

      commit('SET_USER', userData);

      // ✅ NOVO: Učitaj notifikacije kada se user učita iz postojećeg token-a
      try {
        console.log(
          '[Auth Action] Loading notifications after fetching current user...'
        );
        await dispatch('notifications/loadNotifications', null, { root: true });
        console.log(
          '[Auth Action] Notifications loaded successfully after fetching user'
        );
      } catch (notificationError) {
        console.error(
          '[Auth Action] Error loading notifications after fetching user:',
          notificationError
        );
        // Ne prekidaj proces zbog greške sa notifikacijama
      }

      return userData;
    } catch (error) {
      console.error('[Auth Action] Error fetching user:', error);
      throw error;
    }
  },

  setLoggedIn(
    { commit }: ActionContext<AuthState, RootState>,
    status: boolean
  ) {
    console.log('[Auth Action] setLoggedIn called with status:', status);
    commit('SET_LOGGED_IN', status);
  },

  // Akcija koja prvo proverava da li je korisnik već učitan
  ensureCurrentUser({ dispatch, state }: ActionContext<AuthState, RootState>) {
    if (state.token && !state.user) {
      console.log(
        '[Auth Action] Token exists but no user, fetching current user'
      );
      return dispatch('fetchCurrentUser');
    } else {
      console.log('[Auth Action] User already loaded or no token');
      return Promise.resolve(state.user);
    }
  },
};

// Mutacije
const mutations = {
  SET_TOKEN(state: AuthState, token: string | null) {
    console.log('[Auth Mutation] SET_TOKEN:', { tokenExists: !!token });
    state.token = token;
  },
  SET_USER(state: AuthState, user: User | null) {
    console.log('[Auth Mutation] SET_USER:', {
      userBefore: state.user ? { id: state.user.id } : null,
      userAfter: user ? { id: user.id } : null,
    });
    state.user = user;
  },
  SET_LOGGED_IN(state: AuthState, status: boolean) {
    console.log('[Auth Mutation] SET_LOGGED_IN:', status);
    state.isLoggedIn = status;
  },
  SET_LOADING(state: AuthState, isLoading: boolean) {
    console.log('[Auth Mutation] SET_LOADING:', isLoading);
    state.isLoading = isLoading;
  },
  SET_LOGIN_ERROR(state: AuthState, error: string | null) {
    console.log('[Auth Mutation] SET_LOGIN_ERROR:', error);
    state.loginError = error;
  },
};

// Eksportujemo Vuex modul
const authModule: Module<AuthState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default authModule;
